import { useQueryClient } from '@tanstack/react-query'
import { Bell, Camera, Download, IdCard, LogOut, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { CameraStatusDot } from '@/components/commons'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ROUTES } from '@/constants/routes'
import { useCameraPermission } from '@/hooks/use-camera-permission'
import { usePushPermission } from '@/hooks/use-push-notifications'
import { usePwaInstall } from '@/hooks/use-pwa-install'
import { disablePush } from '@/lib/push'
import { useAuthStore, useProfileStore, useVirtualBadgeStore } from '@/store'
import { getCameraErrorMessage } from '@/utils/camera-error'
import { Button } from '../ui/button'

export const UserMenu = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { logout, user } = useAuthStore()
	const { defaultBranchName, defaultContractName, defaultCustomerName } =
		useProfileStore()
	const queryClient = useQueryClient()
	const openVirtualBadge = useVirtualBadgeStore((s) => s.open)
	const { canInstall, install } = usePwaInstall()
	const { permission: pushPermission, enable: enablePush } = usePushPermission()
	const camera = useCameraPermission()

	// Chamado direto do toque no item: é o que o iOS exige para exibir o aviso da câmera
	const handleCamera = async () => {
		if (camera.state === 'denied' || camera.state === 'unsupported') {
			toast.error(t(`qrScanner.permission.${camera.state}Hint`), {
				autoClose: 10000,
			})
			return
		}
		const result = await camera.request()
		camera.release() // aqui só confirmamos a permissão; o scanner abre a câmera quando for usado
		if (result.granted) {
			toast.success(t('qrScanner.permission.granted'))
		} else if (result.error) {
			toast.error(getCameraErrorMessage(result.error, t), {
				autoClose: 10000,
			})
		}
	}

	const handleLogout = async () => {
		// Precisa rodar antes do logout, enquanto a sessão ainda autentica a chamada
		await disablePush()
		logout()
		queryClient.clear()
		navigate(ROUTES.PUBLIC.LOGIN)
	}

	return (
		<div className="flex items-center gap-2">
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger aria-label={t('userMenu.openMenu')} asChild>
					<Button
						variant="ghost"
						className="flex h-10 w-10 md:h-10 md:w-auto md:px-2 items-center gap-2 p-0 text-kaefer-silver hover:bg-white/10 hover:text-kaefer-silver"
					>
						<span className="hidden flex-col items-end md:flex">
							<span className="max-w-32 lg:max-w-48 truncate font-medium text-sm text-white leading-tight">
								{user?.name || ''}
							</span>
							<span className="max-w-32 lg:max-w-48 truncate text-white/70 text-xs leading-tight">
								{user?.badge || ''}
							</span>
						</span>
						<User className="size-6 shrink-0 text-kaefer-silver" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					className="w-[280px] border-kaefer-light-silver bg-white shadow-lg p-1"
				>
					{/* Cabeçalho do Usuário */}
					<DropdownMenuLabel className="font-normal px-3 pt-3 pb-2">
						<div className="flex flex-col gap-3">
							{/* Nome e Matrícula */}
							<div className="flex items-center gap-2.5">
								<div className="min-w-0">
									<p
										className="truncate font-semibold text-kaefer-dark-grey text-sm leading-snug"
										title={user?.name}
									>
										{user?.name || 'Usuário'}
									</p>
									<p className="text-kaefer-medium-grey text-xs leading-snug">
										{t('userMenu.badge')}: {user?.badge || ''}
									</p>
								</div>
							</div>

							{/* Acesso padrão (Filial + Contrato) */}
							{(defaultBranchName || defaultContractName) && (
								<div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col gap-1.5">
									<p className="text-[10px] font-bold uppercase tracking-wider text-kaefer-sapphire-blue leading-none">
										{t('userMenu.defaultContext')}
									</p>
									{defaultBranchName && (
										<div>
											<p className="text-[10px] text-slate-400 leading-none mb-0.5">
												{t('userMenu.branch')}
											</p>
											<p
												className="text-xs text-kaefer-dark-grey leading-tight truncate"
												title={defaultBranchName}
											>
												{defaultBranchName}
											</p>
										</div>
									)}
									{defaultContractName && (
										<div>
											<p className="text-[10px] text-slate-400 leading-none mb-0.5">
												{t('userMenu.contract')}
											</p>
											<p
												className="text-xs text-kaefer-dark-grey leading-tight line-clamp-2"
												title={defaultContractName}
											>
												{defaultContractName}
											</p>
										</div>
									)}
									{defaultCustomerName && (
										<div>
											<p className="text-[10px] text-slate-400 leading-none mb-0.5">
												{t('inspections.common.customer')}
											</p>
											<p
												className="text-xs text-kaefer-dark-grey leading-tight line-clamp-2"
												title={defaultCustomerName}
											>
												{defaultCustomerName}
											</p>
										</div>
									)}
								</div>
							)}

							{/* Lotação ERP — só exibe se diferente do contrato padrão */}
							{user?.crName && user.crName !== defaultContractName && (
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none mb-0.5">
										{t('userMenu.erpAssignment')}
									</p>
									<p
										className="text-xs text-kaefer-medium-grey leading-tight line-clamp-2"
										title={user.crName}
									>
										{user.crName}
									</p>
								</div>
							)}
						</div>
					</DropdownMenuLabel>

					<DropdownMenuSeparator className="bg-kaefer-light-silver mx-2" />

					{/* Opções do Menu */}
					<div className="p-1">
						<DropdownMenuItem
							onClick={() => navigate(ROUTES.DASHBOARD.PROFILE)}
							className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 focus:bg-kaefer-light-silver/50 focus:text-kaefer-dark-grey"
						>
							<User className="h-4 w-4 text-kaefer-medium-grey" />
							<span className="font-medium text-sm">
								{t('userMenu.profile')}
							</span>
						</DropdownMenuItem>

						<DropdownMenuItem
							onClick={openVirtualBadge}
							className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 focus:bg-kaefer-light-silver/50 focus:text-kaefer-dark-grey"
						>
							<IdCard className="h-4 w-4 text-kaefer-medium-grey" />
							<span className="font-medium text-sm">
								{t('userMenu.virtualBadge')}
							</span>
						</DropdownMenuItem>

						<DropdownMenuItem
							onClick={handleCamera}
							className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 focus:bg-kaefer-light-silver/50 focus:text-kaefer-dark-grey"
						>
							<Camera className="h-4 w-4 text-kaefer-medium-grey" />
							<span className="font-medium text-sm">
								{t('userMenu.camera')}
							</span>
							<span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
								<CameraStatusDot state={camera.state} />
								{t(`qrScanner.permission.${camera.state}`)}
							</span>
						</DropdownMenuItem>

						{pushPermission === 'default' && (
							<DropdownMenuItem
								onClick={enablePush}
								className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 focus:bg-kaefer-light-silver/50 focus:text-kaefer-dark-grey"
							>
								<Bell className="h-4 w-4 text-kaefer-medium-grey" />
								<span className="font-medium text-sm">
									{t('pwa.enablePush')}
								</span>
							</DropdownMenuItem>
						)}

						{canInstall && (
							<DropdownMenuItem
								onClick={install}
								className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 focus:bg-kaefer-light-silver/50 focus:text-kaefer-dark-grey"
							>
								<Download className="h-4 w-4 text-kaefer-medium-grey" />
								<span className="font-medium text-sm">
									{t('pwa.installApp')}
								</span>
							</DropdownMenuItem>
						)}

						<DropdownMenuItem
							onClick={handleLogout}
							className="cursor-pointer flex items-center gap-2 rounded-md py-2 px-3 text-kaefer-red focus:bg-red-50 focus:text-kaefer-red"
						>
							<LogOut className="h-4 w-4" />
							<span className="font-medium text-sm">
								{t('userMenu.logout')}
							</span>
						</DropdownMenuItem>
					</div>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}
